import { type StartApplePayFlowParams } from '../../actions/payment/startApplePayFlow';
import { type AuthParams } from '../../types/AuthParams';
import { onClickHandler } from '../utils/onClickHandler';
import { createApplePayPaymentRequest } from './applePayPaymentRequest';
import { validateMerchantHandler } from './validateMerchantHandler';
import { shippingContactSelectedHandler } from './shippingContactSelectedHandler';
import { shippingMethodSelectedHandler } from './shippingMethodSelectedHandler';
import { cancelHandler } from './cancelHandler';
import { paymentAuthorizeHandler } from './paymentAuthorizedHandler';
import { getApplePayVersionToUse } from './getApplePayVersionToUse';
import { paymentMethodSelectedHandler } from './paymentMethodSelectedHandler';

const noop: any = () => undefined;
/**
 * Initializes Apple Pay Payment Request and create Apple Pay Session
 */
export const initApplePaySessionJsSdk = async (
  {
    meta,
    sessionId,
    countryCode,
    currencyCode,
    domain,
    paymentLabel,
    paymentAmount,
    paymentBreakdown,
    shippingMethods,
    merchantCapabilities,
    supportedNetworks,
    requestShipping,
    biLogger,
    fedopsLogger,
    experiments,
    onClick,
    onShippingContactSelected,
    onShippingMethodSelected,
    onPaymentAuthorized,
    getInitialPaymentDataWithBi,
  }: StartApplePayFlowParams,
  auth: AuthParams,
) => {
  /**
   * Creating ApplePaySession and handling callbacks should be in sync
   * between Thunderbolt implementation
   * (https://github.com/wix-private/thunderbolt/blob/master/packages/feature-tpa/src/handlers/applePay/index.ts, "./initApplePaySessionJsSdk.ts")
   * for in-iframe solutions (e.g. Ecom Checkout page),
   * and cases when e-widget is in the top window (e.g. Ecom Product page, Ecom Cart page) "./initApplePaySession"
   */

  const request = createApplePayPaymentRequest({
    countryCode,
    currencyCode,
    paymentLabel,
    paymentAmount,
    paymentBreakdown,
    shippingMethods,
    merchantCapabilities,
    supportedNetworks,
    requestShipping,
    experiments,
  });
  return new Promise<void>((resolve, reject) => {
    window.Wix.Pay.ApplePay.startSession(
      {
        applePayVersion: getApplePayVersionToUse(),
        paymentRequest: request,
        onClick: onClickHandler({
          onClick,
          logErrorParams: {
            message: '[ApplePay] this.props.onClick() throws exception',
            fingerprint: ['applePay', 'onClick'],
          },
        }),
        onValidateMerchant: validateMerchantHandler({ meta, domain }, biLogger, auth, fedopsLogger),
        onShippingContactSelected: shippingContactSelectedHandler(
          onShippingContactSelected,
          { currencyCode, paymentAmount, paymentLabel, meta },
          biLogger,
          getInitialPaymentDataWithBi,
        ),
        onShippingMethodSelected: shippingMethodSelectedHandler(
          onShippingMethodSelected,
          {
            appInstanceId: meta.appInstanceId,
            paymentLabel,
            currencyCode,
          },
          biLogger,
        ),
        onPaymentAuthorized: paymentAuthorizeHandler(
          onPaymentAuthorized,
          {
            appInstanceId: meta.appInstanceId,
            msid: meta.siteId,
            currencyCode,
            shippingMethods,
            sessionId,
          },
          biLogger,
          auth,
        ),
        onPaymentMethodSelected: paymentMethodSelectedHandler(
          getInitialPaymentDataWithBi,
          {
            appInstanceId: meta.appInstanceId,
            currencyCode,
            paymentLabel,
          },
          biLogger,
        ),
        onCouponCodeChanged: noop,
        onCancel: cancelHandler({
          biLogger,
          biData: {
            paymentAmount,
            currencyCode,
          },
        }),
      },
      resolve,
      (e) => {
        console.error(e);
        reject(e);
      },
    );
  });
};
